import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { PHONE_HREF, PHONE_DISPLAY, sendEvent } from "@/lib/tracking";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/group-personal-training-windermere", label: "Coaching" },
  { to: "/gym-windermere", label: "Gym" },
  {
    label: "Life at KORE",
    to: "/#life-at-kore",
    children: [
      { to: "/#life-at-kore", label: "Life at KORE", note: "Training, community, the club" },
      { to: "/classes", label: "Classes", note: "Ascent, Zones, Blast, Kombat" },
      { to: "/womens-strength-foundations", label: "Women's Strength Foundations", note: "Women-only beginners' group" },
      { to: "/fuel-chapters", label: "Fuel Chapters", note: "Thursday nutrition group with Georgie" },
    ],
  },
  { to: "/about", label: "About" },
];

const linkCls = ({ isActive }) => `text-[15px] font-medium transition-colors min-h-[44px] inline-flex items-center ${isActive ? "text-kore-forest" : "text-kore-ink/70 hover:text-kore-forest"}`;

function Dropdown({ item }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="nav-life-at-kore"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-[15px] font-medium text-kore-ink/70 hover:text-kore-forest transition-colors min-h-[44px] inline-flex items-center gap-1"
      >
        {item.label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" data-testid="nav-life-menu" className="absolute left-0 top-full pt-2 w-80">
          <div className="bg-white border border-kore-stone rounded-xl shadow-lift p-2">
            {item.children.map((c) => (
              <Link key={c.to} to={c.to} role="menuitem" data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setOpen(false)} className="block rounded-lg px-4 py-3 hover:bg-kore-warm transition-colors">
                <div className="text-[15px] font-semibold text-kore-forest">{c.label}</div>
                <div className="text-[13px] text-kore-ink/60 mt-0.5">{c.note}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();
  const minimal = pathname === "/30-day-trial";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname, hash]);
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [pathname, hash]);

  return (
    <header data-testid="site-navbar" className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${scrolled || open ? "bg-kore-warm/95 backdrop-blur-md border-b border-kore-stone" : "bg-kore-warm/70 backdrop-blur-sm"}`}>
      <div className="container-kore flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center text-kore-forest"><Logo size="md" asLink={false} /></Link>

        {!minimal && <nav aria-label="Main" className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => l.children
            ? <Dropdown key={l.label} item={l} />
            : <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>{l.label}</NavLink>
          )}
        </nav>}

        <div className="flex items-center gap-3">
          <a href={PHONE_HREF} data-testid="nav-phone" onClick={() => sendEvent("phone_click", { label: "navbar" })} className="hidden xl:inline-flex items-center gap-2 text-[14px] text-kore-ink/70 hover:text-kore-forest transition-colors min-h-[44px]">
            <Phone size={14} /> {PHONE_DISPLAY}
          </a>
          {minimal ? (
            <a href="#apply" data-testid="nav-join-now" className="btn-primary max-md:!hidden !py-2.5 !px-5 text-[14px]">Check Trial Availability</a>
          ) : (
            <Link to="/30-day-trial" data-testid="nav-join-now" className="btn-primary max-md:!hidden !py-2.5 !px-5 text-[14px]">Explore the £99 Trial</Link>
          )}
          {!minimal && <button data-testid="nav-mobile-toggle" className="lg:hidden inline-flex items-center gap-2 min-h-[44px] px-3 text-[14px] font-semibold text-kore-forest" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"}>
            {open ? <X size={20} /> : <Menu size={20} />} <span>{open ? "Close" : "Menu"}</span>
          </button>}
        </div>
      </div>

      {open && (
        <div id="mobile-menu" data-testid="nav-mobile-panel" className="lg:hidden bg-kore-warm border-t border-kore-stone max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav aria-label="Mobile" className="container-kore py-6 flex flex-col">
            {LINKS.flatMap((l) => l.children ? l.children : [l]).map((l) => (
              <Link key={l.to} to={l.to} className="text-lg font-semibold text-kore-forest py-3 border-b border-kore-stone min-h-[44px]">{l.label}</Link>
            ))}
            <Link to="/contact" className="text-lg font-semibold text-kore-forest py-3 border-b border-kore-stone min-h-[44px]">Contact</Link>
            <a href={PHONE_HREF} data-testid="nav-mobile-phone" onClick={() => sendEvent("phone_click", { label: "navbar_mobile" })} className="mt-5 text-[15px] text-kore-ink/70 inline-flex items-center gap-2 min-h-[44px]"><Phone size={15} /> {PHONE_DISPLAY}</a>
            <Link to="/30-day-trial" className="btn-primary self-start mt-3">Explore the £99 Trial</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
